import React, { useCallback, useMemo } from 'react';

import {
    SlotPluginIframe as HostSlotPluginIframe,
    resolveSlotPluginEntrypointUrl,
    type PluginTheme,
    type SlotPluginDescriptor,
    type SlotPluginTitle,
} from '@yandex-360/360-plugin-host';
import { useTheme } from '@yandex-360/orb-components/dist/components/Theme';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../../../init-store';
import Button from '../../../../../shared/components/button';
import SvgImage from '../../../../../shared/components/svg-image';
import { SELF } from '../../../../../shared/constants';
import { selectSlotPluginsConfigUrl } from '../../../../../shared/features/config/selectors';
import { selectIsSlotPluginsEnabled } from '../../../../../shared/features/flags/selectors';
import { useSlotPluginDescriptors } from '../../../../../shared/features/slot-plugins';
import {
    getUserSelector,
    isYandexTeamUserSelector,
    selectUserUid,
} from '../../../../../shared/features/user/selectors';
import { i18n } from '../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../shared/helpers/metrika';
import { ConferenceStatus } from '../../../constants';
import {
    getConferenceConfig,
    getConferenceId,
    getConferenceStatus,
    getMyUserId,
    getParticipant,
    getRoomId,
} from '../../../selectors';
import { setSidebarContent } from '../../sidebar/slice';
import { TELEMOST_CALL_SIDEBAR_SLOT_ID } from '../constants';
import { TelemostCallSlotPluginContext } from '../types';
import styles from './styles.css';

const closeIcon = <SvgImage imageName="close2" />;

function resolvePluginUrl(plugin: SlotPluginDescriptor): string | null {
    try {
        return resolveSlotPluginEntrypointUrl(plugin);
    } catch {
        return null;
    }
}

function resolvePluginTitle(localizedTitle: SlotPluginTitle | undefined, fallback: string): string {
    if (!localizedTitle) {
        return fallback;
    }

    const appLangTitle = LANG === 'en' ? localizedTitle.en : localizedTitle.ru;

    return appLangTitle || localizedTitle.ru || localizedTitle.en || fallback;
}

export function PluginsSidebar() {
    const dispatch = useDispatch();
    const theme = useTheme('dark');
    const enabled = useSelector(selectIsSlotPluginsEnabled);
    const configUrl = useSelector(selectSlotPluginsConfigUrl);
    const conferenceStatus = useSelector(getConferenceStatus);
    const conferenceConfig = useSelector(getConferenceConfig);
    const conferenceId = useSelector(getConferenceId);
    const roomId = useSelector(getRoomId);
    const myUserId = useSelector(getMyUserId);
    const user = useSelector(getUserSelector);
    const userId = useSelector(selectUserUid);
    const isYateam = useSelector(isYandexTeamUserSelector);
    const selfParticipant = useSelector((state: RootState) => getParticipant(state, SELF));

    const { plugins, loading } = useSlotPluginDescriptors(TELEMOST_CALL_SIDEBAR_SLOT_ID, enabled, configUrl);

    const pluginEntries = useMemo(() => {
        if (!enabled) {
            return [];
        }

        return plugins.flatMap((plugin) => {
            const pluginUrl = resolvePluginUrl(plugin);

            return pluginUrl ? [{ plugin, pluginUrl }] : [];
        });
    }, [enabled, plugins]);

    const resolveTheme = useCallback((): PluginTheme => theme || 'dark', [theme]);

    const context = useMemo<TelemostCallSlotPluginContext>(
        () => ({
            theme: resolveTheme(),
            meeting: {
                roomId,
                conferenceId,
                uri: conferenceConfig?.uri,
                resolvedUri: conferenceConfig?.resolved_uri,
                displayName: conferenceConfig?.display_name,
                status: conferenceStatus,
            },
            currentUser: user
                ? {
                      uid: user.uid,
                      login: user.login,
                      name: user.name,
                      displayName: user.displayName,
                      timezone: user.timezone,
                      organizationIds: user.organizationIds,
                      isYandexTeamUser: user.isYandexTeamUser,
                  }
                : null,
            currentParticipant:
                conferenceStatus === ConferenceStatus.JOINED
                    ? {
                          id: selfParticipant.id,
                          peerId: myUserId,
                          uid: selfParticipant.uid,
                          displayName: selfParticipant.displayName,
                          isModerator: selfParticipant.isModerator,
                          isOwner: selfParticipant.isOwner,
                      }
                    : null,
        }),
        [conferenceConfig, conferenceId, conferenceStatus, myUserId, resolveTheme, roomId, selfParticipant, user]
    );

    const entityMeta = useMemo(() => {
        const result: Record<string, string> = {};

        if (roomId) {
            result.roomId = roomId;
        }

        if (conferenceId) {
            result.conferenceId = conferenceId;
        }

        return result;
    }, [conferenceId, roomId]);

    const onCloseButtonClick = useCallback(() => {
        countMetrika('meeting_screen', 'plugins_sidebar', 'close');
        dispatch(setSidebarContent(null));
    }, [dispatch]);

    const title = i18n('toolbar', 'plugins-button-title');
    const emptyText = i18n('slot-plugins', 'empty');
    const loadingText = i18n('slot-plugins', 'loading');
    const loadErrorText = i18n('slot-plugins', 'load-error');

    return (
        <div className={styles.pluginsSidebar}>
            <Button
                className={styles.close}
                view="neutral-transparent"
                size="sm"
                onClick={onCloseButtonClick}
                icon={closeIcon}
            />
            <div className={styles.head}>
                <h1 className={styles.title}>{title}</h1>
            </div>
            <div className={styles.content}>
                {loading ? <div className={styles.placeholder}>{loadingText}</div> : null}
                {!loading && !pluginEntries.length ? <div className={styles.placeholder}>{emptyText}</div> : null}
                {pluginEntries.map(({ plugin, pluginUrl }) => {
                    const pluginTitle = resolvePluginTitle(plugin.title, plugin.slug);

                    return (
                        <div className={styles.plugin} key={`${plugin.slug}@${plugin.version}`}>
                            <div className={styles.pluginTitle}>{pluginTitle}</div>
                            <HostSlotPluginIframe<TelemostCallSlotPluginContext>
                                title={`${title}: ${pluginTitle}`}
                                src={pluginUrl}
                                service="telemost"
                                slot={TELEMOST_CALL_SIDEBAR_SLOT_ID}
                                pluginId={plugin.slug}
                                entityId={roomId || conferenceId}
                                entityMeta={entityMeta}
                                context={context}
                                userId={userId}
                                isYateam={isYateam}
                                resolveTheme={resolveTheme}
                                className={styles.pluginIframe}
                                initialSize={{ width: '100%', height: 360 }}
                                scrolling="auto"
                                onClose={onCloseButtonClick}
                                renderLoading={() => <div className={styles.pluginState}>{loadingText}</div>}
                                renderError={(error: string) => (
                                    <div className={styles.pluginState}>
                                        {loadErrorText}
                                        {error ? `: ${error}` : ''}
                                    </div>
                                )}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
